import { type IClause, type ILaw, type INode } from '@/lib/ris-types'
import Title from './Title/Title'
import { type FunctionComponent } from 'react'
import Paragraph from './Paragraph/Paragraph'
import Fallback from './Fallback'
import List from './List/List'
import Table from './Table/Table'
import { ExternalLink, Paperclip } from 'lucide-react'
import Button from '@/components/Button/Button'
import { Split, Stack } from '@christiankaindl/lyts'
import Text from '@/components/Text/Text'
import { theme } from '@/styles/theme.css'

interface Props {
  clause: IClause
  index: number
  norm?: ILaw
  renderTitle?: boolean
  richRefLinks?: boolean
}

export default function ClauseServer ({ clause, renderTitle = true, richRefLinks = true, norm }: Props) {
  return (
    <>
    {/* Some clauses don't have any title, so in that case render a "fake" title so we get the menu and linking, etc. */}
      {renderTitle && !clause?.title && !clause.children?.[0]?.type?.startsWith('ueberschrift') && (
          <Title type='ueberschrift.para' index={0} norm={norm} clause={clause} />
      )}
      {clause.children?.map((child, index) => {
        if (!renderTitle && child.type?.startsWith('ueberschrift')) {
          return null
        }
        return (
          <SwitchTypes
            key={`${clause.clauseId}-${index}`}
            child={child}
            index={index}
            norm={norm}
            clause={clause}
            richRefLinks={richRefLinks}
          />
        )
      })}
      {clause.documents && clause.documents.length > 0 && (
        <Stack gap='1px' style={{ borderRadius: 15, padding: 15, backgroundColor: theme.colors.backgrounds.bgA1 }}>
          <Text color='secondary' size='small'>
            Anhänge
          </Text>
          {clause.documents?.map((doc) => {
            return (
              <Button
                title='Anhang in neuem Tab öffnen'
                style={{ width: 'auto', marginLeft: -6, marginRight: -6 }}
                variant='flat'
                key={doc.Name}
                href={doc.Urls?.Url ?? doc.Urls?.ContentUrl?.Url}
              >
                <Paperclip size={16} />
                {doc.Name}
                <Split />
                <span style={{ fontWeight: 'normal', opacity: 0.4 }}>{(doc.Urls?.DataType ?? doc.Urls?.ContentUrl?.DataType)?.toUpperCase()}</span>
                <ExternalLink size={16} style={{ opacity: 0.4 }} />
              </Button>
            )
          })}
        </Stack>
      )}
    </>
  )
}

export const SwitchTypes: FunctionComponent<{ child: INode, index: number, norm?: ILaw, clause: IClause, richRefLinks?: boolean }> = function ({ child, index, norm, clause, richRefLinks }) {
  if (typeof child === 'string') {
    return (
      <Paragraph value={child} />
    )
  } else if (child.type?.startsWith('ueberschrift') || child.type === 'h1' || child.type === 'h2' || child.type === 'h3' || child.type === 'h4' || child.type === 'h5' || child.type === 'h6') {
    return (
      <Title {...child} index={index} norm={norm} clause={clause} />
    )
  } else if (child.type?.startsWith('absatz') || child.type === 'p') {
    return (
      <Paragraph {...child} richRefLinks={richRefLinks} />
    )
  } else if (child.type?.startsWith('table')) {
    return (
      <Table {...child} />
    )
  } else if (child.type?.startsWith('liste')) {
    return (
      <List {...child} />
    )
  } else if (child.type?.startsWith('abstand')) {
    return (
      null
    )
  } else if (child.type?.startsWith('inhaltsvz')) {
    return null
  } else {
    return (
      <Fallback {...child} />
    )
  }
}
